import { useState } from "react";
import { Bell, CheckCheck } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { PermissionGate } from "@/components/rbac/PermissionGate";
import {
  useBroadcastNotification,
  useSendToUserNotification,
  useNotifications,
  useMarkAllRead,
  useMarkRead,
  type AppNotification,
} from "@/hooks/useNotifications";
import { toast } from "sonner";

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return day < 7
    ? `${day}d ago`
    : new Date(iso).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
      });
}

const NotificationRow = ({
  n,
  onRead,
}: {
  n: AppNotification;
  onRead: (id: string) => void;
}) => (
  <div
    className={`flex items-start gap-3 px-4 py-3 border-b border-border last:border-0 hover:bg-accent/40 transition-colors ${
      !n.isRead ? "bg-primary/5" : ""
    }`}
  >
    <div
      className={`mt-2 w-2 h-2 rounded-full flex-shrink-0 ${n.isRead ? "bg-transparent" : "bg-primary"}`}
    />
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-foreground">{n.title}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{n.body}</p>
      <p className="text-xs text-muted-foreground/60 mt-1">
        {relativeTime(n.sentAt)}
      </p>
    </div>
    {!n.isRead && (
      <button
        type="button"
        onClick={() => onRead(n.id)}
        className="text-xs text-muted-foreground hover:text-foreground transition-colors flex-shrink-0 mt-1"
      >
        Mark read
      </button>
    )}
  </div>
);

export const NotificationsPage = () => {
  const [tab, setTab] = useState<"history" | "broadcast" | "test">("history");
  const [audience, setAudience] = useState<
    "ALL_CUSTOMERS" | "ALL_PROVIDERS" | "ALL_USERS"
  >("ALL_CUSTOMERS");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [targetUserId, setTargetUserId] = useState("");

  const { data, isLoading } = useNotifications(1);
  const notifications = data?.data ?? [];
  const unreadCount = data?.unreadCount ?? 0;

  const markRead = useMarkRead();
  const markAll = useMarkAllRead();
  const broadcast = useBroadcastNotification();
  const sendToUser = useSendToUserNotification();

  const send = async () => {
    try {
      if (tab === "test") {
        if (!targetUserId.trim()) {
          toast.error("Please enter a target User ID");
          return;
        }
        await sendToUser.mutateAsync({
          userId: targetUserId.trim(),
          title: title || "Test Notification",
          body: body || "This is a test notification from Admin Panel",
        });
        toast.success("Test notification sent directly to user");
      } else {
        await broadcast.mutateAsync({ audience, title, body });
        toast.success("Notification queued");
      }
      setTitle("");
      setBody("");
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  return (
    <AppLayout title="Notifications">
      <div className="flex gap-2 mb-4">
        {(["history", "broadcast", "test"] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`px-4 py-1.5 text-sm rounded-btn capitalize transition-colors ${
              tab === t
                ? "bg-primary text-primary-foreground"
                : "border border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            {t === "history"
              ? `History${unreadCount > 0 ? ` (${unreadCount})` : ""}`
              : t === "broadcast"
                ? "Broadcast"
                : "Send Direct / Test Push"}
          </button>
        ))}
      </div>

      {tab === "history" && (
        <div className="bg-card rounded-card border border-border overflow-hidden max-w-2xl">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <span className="text-sm font-medium text-foreground">
              {unreadCount > 0 ? `${unreadCount} unread` : "All caught up"}
            </span>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => markAll.mutate()}
                disabled={markAll.isPending}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                Mark all read
              </button>
            )}
          </div>
          {isLoading && (
            <div className="py-10 text-center text-sm text-muted-foreground">
              Loading…
            </div>
          )}
          {!isLoading && notifications.length === 0 && (
            <div className="py-12 text-center">
              <Bell className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">
                No notifications yet
              </p>
            </div>
          )}
          {notifications.map((n) => (
            <NotificationRow
              key={n.id}
              n={n}
              onRead={(id) => markRead.mutate(id)}
            />
          ))}
        </div>
      )}

      {tab === "broadcast" && (
        <div className="bg-card rounded-card border border-border p-5 max-w-2xl space-y-4">
          <div>
            <label className="text-sm font-medium text-foreground">
              Audience
            </label>
            <select
              value={audience}
              onChange={(e) => setAudience(e.target.value as typeof audience)}
              className="mt-1 w-full rounded-btn border border-border bg-background px-3 py-2 text-sm"
            >
              <option value="ALL_CUSTOMERS">All customers</option>
              <option value="ALL_PROVIDERS">All providers</option>
              <option value="ALL_USERS">All users</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-foreground">Title</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1 w-full rounded-btn border border-border bg-background px-3 py-2 text-sm"
              placeholder="e.g. Planned maintenance tonight"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-foreground">Body</label>
            <textarea
              rows={4}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="mt-1 w-full rounded-btn border border-border bg-background px-3 py-2 text-sm"
              placeholder="Message content…"
            />
          </div>
          <PermissionGate module="notifications" action="broadcast">
            <button
              type="button"
              disabled={!title || !body || broadcast.isPending}
              onClick={send}
              className="px-4 py-2 text-sm font-medium rounded-btn bg-primary text-primary-foreground disabled:opacity-50"
            >
              {broadcast.isPending ? "Sending…" : "Send broadcast"}
            </button>
          </PermissionGate>
        </div>
      )}

      {tab === "test" && (
        <div className="bg-card rounded-card border border-border p-5 max-w-2xl space-y-4">
          <div>
            <label className="text-sm font-medium text-foreground">
              Target User ID
            </label>
            <input
              value={targetUserId}
              onChange={(e) => setTargetUserId(e.target.value)}
              className="mt-1 w-full rounded-btn border border-border bg-background px-3 py-2 text-sm font-mono"
              placeholder="e.g. user_id_12345"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-foreground">Title</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1 w-full rounded-btn border border-border bg-background px-3 py-2 text-sm"
              placeholder="Test Notification Title"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-foreground">Body</label>
            <textarea
              rows={3}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="mt-1 w-full rounded-btn border border-border bg-background px-3 py-2 text-sm"
              placeholder="Test push notification message..."
            />
          </div>
          <PermissionGate module="notifications" action="broadcast">
            <button
              type="button"
              disabled={!targetUserId || sendToUser.isPending}
              onClick={send}
              className="px-4 py-2 text-sm font-medium rounded-btn bg-primary text-primary-foreground disabled:opacity-50"
            >
              {sendToUser.isPending
                ? "Sending Push…"
                : "Send Test Push Notification"}
            </button>
          </PermissionGate>
        </div>
      )}
    </AppLayout>
  );
};

export default NotificationsPage;
